import { Link } from 'react-router-dom';
import { featuredVideos } from '@/mocks/videos';
import { useRef, useState, useEffect } from 'react';

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startAutoPlay = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredVideos.length);
    }, 5000);
  };

  useEffect(() => {
    startAutoPlay();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    startAutoPlay();
  };

  const video = featuredVideos[currentSlide];

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredVideos.length) % featuredVideos.length);
    startAutoPlay();
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredVideos.length);
    startAutoPlay();
  };

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
      </div>

      <div className="absolute inset-0 flex items-end pb-16 md:pb-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl">
            <span className="inline-block px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-full mb-3">
              {video.category}
            </span>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-white mb-3">
              {video.title}
            </h1>
            <div className="flex items-center gap-4 text-white/80 text-sm flex-wrap mb-4">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                {video.rating}
              </span>
              <span>{video.year}</span>
              <span>{video.duration}</span>
              <span>{video.type}</span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed line-clamp-2 mb-5 max-w-xl hidden md:block">
              {video.description}
            </p>
            <Link
              to={`/article/${video.id}`}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full text-sm transition-all whitespace-nowrap"
            >
              <i className="ri-play-fill"></i>
              立即观看
            </Link>
          </div>
        </div>
      </div>

      <button
        onClick={handlePrev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-all"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-s-line text-white text-lg"></i>
      </button>
      <button
        onClick={handleNext}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-all"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-s-line text-white text-lg"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {featuredVideos.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all rounded-full ${
              index === currentSlide
                ? 'w-6 h-2 bg-primary-500'
                : 'w-2 h-2 bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}